import { ResourceTier, ShipClass, RaceId, AsteroidSize } from '../types/entities.js';
import { PlayerRank } from '../types/player.js';
export const MASTER_SEED = 0x564952414C; // "VIRAL" in hex
export const TICK_RATE = 20;
export const TICK_MS = 1000 / TICK_RATE;
/**
 * Side length of a single sector in world units.
 *
 * This is intentionally large so that planets, asteroid fields and stations
 * end up far apart inside the same sector — a player should rarely be able
 * to see two planets at once from one vantage point. Increase/decrease in
 * sync with `INTEREST_RADIUS` and the client fog range to keep the feeling
 * of deep, mostly-empty space.
 */
export const SECTOR_SIZE = 4096;
/**
 * Radius around a player in which "small" entities (asteroids, drops,
 * projectiles) are replicated. Intentionally much smaller than SECTOR_SIZE
 * so players have to physically travel closer to something to see it.
 */
export const INTEREST_RADIUS = 2000;
export const MAX_ENTITIES_PER_SECTOR = 3000;
export const INNER_RING_RADIUS = 20;
export const OUTER_RING_RADIUS = 50;
export const STATION_SAFE_RADIUS = 200;
/**
 * Zone tier ladder driven by sector distance from origin.
 *
 * `getZoneDifficulty(coord)` returns the **1-indexed** tier number for any
 * sector. Tiers 1–3 are the "near galaxy" the original 12 races inhabit;
 * tiers 4–9 are the new deep-space curve aimed at level-100+ pilots and
 * culminate in the genuinely "infinite" Beyond — a tier with no upper
 * bound where every spawn rolls apex content.
 *
 * Two related arrays stay in lock-step with this ladder:
 *   - `ZONE_TIER_OUTER_RADII[i]` — exclusive outer radius for tier `i+1`,
 *     or `Infinity` for the last tier. Sector tier = first index whose
 *     radius is ≥ the sector's distance from origin.
 *   - `ZONE_TIER_KEYS[i]`        — i18n key suffix for the tier label and
 *     description (e.g. `'contested'`, `'embattled'`, …). The locale
 *     bags carry both `zone.<key>` and `zone.<key>.desc` strings.
 *
 * Editing the radii is safe — only the test in
 * `procedural.test.ts → getZoneDifficulty` and the colour palette in the
 * universe map / station UI hard-code values. All gameplay scaling
 * (NPC density, race depth, item availability) flows through the
 * existing `sectorDifficultyMultiplier` / `sectorDifficultyTier` which
 * are continuous and don't care about the discrete bands.
 */
export const ZONE_TIER_OUTER_RADII = [
    20, // 1: Contested
    50, // 2: Embattled (was "War")
    100, // 3: Void
    200, // 4: Abyss
    400, // 5: Nightmare
    700, // 6: Apex
    1100, // 7: Singularity
    1700, // 8: Transcendent
    Infinity, // 9: Beyond — no ceiling, scaling never stops
];
export const ZONE_TIER_KEYS = [
    'contested',
    'embattled',
    'void',
    'abyss',
    'nightmare',
    'apex',
    'singularity',
    'transcendent',
    'beyond',
];
export const ZONE_TIER_COUNT = ZONE_TIER_OUTER_RADII.length;
export const RESOURCE_DROP_LIFETIME = 30_000;
export const DOCKING_DISTANCE = 100;
export const WARP_GATE_DISTANCE = 60;
/**
 * Hard cap on simultaneously-active missions per pilot. Both the
 * server's MissionTracker (rejecting accepts past this number) and the
 * client UI (showing the SLOTS USED counter and disabling ACCEPT once
 * full) read this value, so changing it here is a single-source-of-
 * truth update.
 */
export const MAX_ACTIVE_MISSIONS = 5;
// ─── Death penalties ──────────────────────────────────────────────────
//
// When a player ship is destroyed (PvE or PvP), the server applies two
// soft penalties:
//   1. A small XP loss, sized off the in-level XP bar so freshly-leveled
//      pilots don't immediately get knocked back down a level. The loss
//      is `max(DEATH_XP_PENALTY_MIN, floor(currentInLevelXp * pct))` and
//      is clamped at the current xp (no negative balance, no de-level).
//   2. A fraction of every resource stack drops at the death position as
//      pickups any other player can collect. Consumables are NOT dropped
//      (they have no world-drop entity).
//
// Both penalties can be skipped by carrying the matching insurance
// consumable (memory_imprint_chip / cargo_lockbox), which is consumed
// on activation. Tutorial deaths bypass both penalties — onboarding is
// not a punishment.
export const DEATH_XP_PENALTY_PCT = 0.05;
export const DEATH_XP_PENALTY_MIN = 25;
export const DEATH_INVENTORY_DROP_FRACTION = 0.25;
/**
 * Radius around a warp gate that counts as a safe zone. Intentionally
 * smaller than `STATION_SAFE_RADIUS` so the gate is a tactical refuge,
 * not a giant no-fire bubble: pilots get a brief reprieve to align and
 * jump, but the area shrinks faster than stations do. Ships inside this
 * radius cannot be damaged by other ships/NPCs, but — unlike the
 * station bubble — they do NOT receive any passive hull or shield
 * regeneration. Warp gates are "quiet corners", not field hospitals.
 */
export const WARP_GATE_SAFE_RADIUS = 80;
/**
 * Controls how sparse warp gates are across the sector grid. A warp gate
 * exists in a sector when *every* axis of its coordinate is a multiple of
 * `WARP_GATE_SECTOR_STRIDE` (or the sector is the origin).
 *
 * This rule guarantees that the Chebyshev (max-axis) distance between any
 * two gate sectors is at least `WARP_GATE_SECTOR_STRIDE`, so gates can
 * never be neighbours on the sector grid. With stride=3 the first ring of
 * non-origin gates sits 3 sectors away (e.g. `(3,0,0)`, `(0,3,0)`,
 * `(3,3,0)`, `(3,3,3)`), forcing players to actually fly through a few
 * sectors before the next warp.
 *
 * An older rule based on Manhattan-sum modulo the stride allowed adjacent
 * gate sectors like `(5,0,-1)` and `(5,0,1)` to both pass, which defeated
 * the "explore before you warp" design.
 */
export const WARP_GATE_SECTOR_STRIDE = 3;
/**
 * Deterministic check: does a given sector host a warp gate at all?
 * Origin is always true so a fresh player can immediately reach a gate.
 */
export function sectorHasWarpGate(coord) {
    if (coord.x === 0 && coord.y === 0 && coord.z === 0)
        return true;
    const s = WARP_GATE_SECTOR_STRIDE;
    return coord.x % s === 0 && coord.y % s === 0 && coord.z % s === 0;
}
export const WARP_BASE_COST = 100;
export const WARP_COST_PER_SECTOR = 25;
// ─── Sector claim / siege tuning ──────────────────────────────────────────
//
// Constants that drive the player-claimable sector system (see
// `types/sector-claim.ts`). PvP parity reminder (repo rule 5): none of
// these are ever multiplied into damage, HP, regen, speed or fire-rate.
// They only drive timers (siege window, cooldown, garrison respawn) and
// economy (claim fee, reinforcement cost, shop discount, owner trickle).
/**
 * How long an attacker must hold the station (after wiping the garrison)
 * before ownership transfers. Intentionally long enough that defenders
 * and allies have a meaningful chance to warp in and interrupt.
 */
export const SIEGE_WINDOW_MS = 15 * 60 * 1000;
/**
 * After an ownership transfer, the sector is "sealed" for this long to
 * prevent the losing party from just warping back and immediately
 * counter-sieging. Fresh siege attempts during this window are rejected
 * by the server.
 */
export const SIEGE_COOLDOWN_MS = 60 * 60 * 1000;
/**
 * Time between the moment the last garrison NPC dies and when the next
 * garrison wave auto-respawns after a successful defence/interruption.
 * Kept relatively short so a successful defend-and-hold doesn't leave
 * the station defenceless for long.
 */
export const GARRISON_RESPAWN_MS = 2 * 60 * 1000;
/**
 * How long a contestant can be absent from the sector before their
 * siege attempt is automatically cancelled. Tuned loose enough that
 * chasing a defender out of view isn't an instant loss, but tight
 * enough that log-offs or warp-aways don't leave siege timers ticking
 * forever.
 */
export const SIEGE_CONTESTANT_ABSENCE_MS = 60 * 1000;
/**
 * Credit cost to claim an unclaimed sector. Scales with the sector's
 * difficulty tier: frontier sectors are cheaper to found a foothold in,
 * deep-space claims cost serious capital. Applied server-side at
 * claim-time so no client-side cheat can bypass it.
 */
export const CLAIM_COST_BY_DIFFICULTY = {
    1: 12_000,
    2: 25_000,
    3: 50_000,
    // Deep-tier claims are gated behind genuinely punishing capital costs —
    // the sectors that gate them carry tier-7+ items and apex-race events,
    // so the price tag has to reflect that you are seizing a piece of the
    // game's late curve rather than another planet patrol.
    4: 120_000,
    5: 250_000,
    6: 500_000,
    7: 1_000_000,
    8: 2_500_000,
    9: 5_000_000,
};
/**
 * Credit cost to bump the garrison level on an owned sector. Index is
 * the *target* level (the level you are buying up to). Level 1 is free
 * — it is the baseline garrison spawned automatically at claim-time.
 */
export const GARRISON_REINFORCE_COST_BY_LEVEL = {
    2: 8_000,
    3: 20_000,
    4: 45_000,
};
/** Maximum allowed garrison level (paid reinforcements cap here). */
export const GARRISON_LEVEL_MAX = 4;
/** Number of defender NPCs spawned per garrison level. */
export const GARRISON_NPCS_PER_LEVEL = 2;
/**
 * Discount applied to the station shop when the buyer is the current
 * sector owner. Purely an economy perk — the underlying item stats are
 * unchanged.
 */
export const OWNER_SHOP_DISCOUNT = 0.10;
/**
 * Fraction of each station-shop credit sink kicked back to the sector
 * owner as passive income. Capped per day server-side to prevent a
 * single lucky whale purchase from flooding the owner with credits.
 */
export const OWNER_REVENUE_SHARE = 0.01;
/** Daily cap (credits) on the owner revenue trickle per owned sector. */
export const OWNER_REVENUE_DAILY_CAP = 10_000;
export function sectorKey(c) {
    return `${c.x},${c.y},${c.z}`;
}
export function parseSectorKey(k) {
    const parts = k.split(',').map(Number);
    return { x: parts[0] ?? 0, y: parts[1] ?? 0, z: parts[2] ?? 0 };
}
/**
 * Prefix used for the per-player tutorial sector. Tutorial sector ids look
 * like `tut_<playerId>` and are never parsed as "x,y,z" coordinates. Any
 * code that persists sectors, lists them on the universe map, or exposes
 * them to the discovery system must treat `isTutorialSectorId(id)` as a
 * skip condition — the tutorial area is private to the onboarding pilot,
 * has no warp gate, and its coordinates are intentionally opaque.
 */
export const TUTORIAL_SECTOR_PREFIX = 'tut_';
/**
 * Sentinel sector coordinate sent to the client when the player is in a
 * tutorial sector. The client keys off this value to suppress the HUD
 * sector name, universe map entry, and discovery side-effects. The number
 * is intentionally large and negative so it cannot collide with any
 * generated sector (which live on the signed-int grid near the origin).
 */
export const TUTORIAL_SECTOR_COORD = { x: -2147483640, y: -2147483640, z: -2147483640 };
export function isTutorialSectorId(sectorId) {
    return sectorId.startsWith(TUTORIAL_SECTOR_PREFIX);
}
export function isTutorialSectorCoord(c) {
    return c.x === TUTORIAL_SECTOR_COORD.x
        && c.y === TUTORIAL_SECTOR_COORD.y
        && c.z === TUTORIAL_SECTOR_COORD.z;
}
/**
 * Half-extent of the cubic training room. The room is centred on the
 * origin inside the per-player tutorial sector, and player ships + the
 * hostile tutorial NPC are clamped to `[-TUTORIAL_ROOM_HALF, +TUTORIAL_ROOM_HALF]`
 * on every axis so the onboarding cannot escape the box.
 */
export const TUTORIAL_ROOM_HALF = 140;
/**
 * One-time XP granted the moment a pilot graduates the tutorial. Sized so
 * that — combined with the front-loaded early curve (level 2 now costs
 * ~113 XP) — finishing onboarding lands a brand-new pilot at level 2 with
 * a little progress into level 3, giving them a "ding" before they're set
 * loose. PvE-only and race-agnostic, so it doesn't affect PvP parity.
 */
export const TUTORIAL_GRADUATION_XP = 150;
export function warpTravelCost(from, to) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return Math.max(0, Math.ceil(WARP_BASE_COST + dist * WARP_COST_PER_SECTOR));
}
export const SELL_PRICE_MULTIPLIER = 0.6;
export function sellPriceForResource(tier, quantity = 1) {
    const info = RESOURCE_INFO[tier];
    if (!info)
        return 0;
    return Math.max(1, Math.floor(info.baseValue * SELL_PRICE_MULTIPLIER)) * quantity;
}
/**
 * Fraction of an equipment item's original credit cost that the player
 * recoups when selling it back to a station. 0.5 means "lose half" — this
 * keeps buying+selling from being a no-risk storage hack while still making
 * an unused impulse purchase feel like it's refundable. Shared constant so
 * the client UI and the server authority agree on the preview number.
 */
export const EQUIPMENT_SELL_PRICE_RATIO = 0.5;
/**
 * Authoritative sell-back price for a weapon or shield of the given original
 * credit cost. Returns 0 when the item is not for sale (shop cost 0 / free
 * default gear) so callers can gate on `> 0` to decide if sell is allowed.
 */
export function sellPriceForEquipment(creditCost) {
    if (!Number.isFinite(creditCost) || creditCost <= 0)
        return 0;
    return Math.max(1, Math.round(creditCost * EQUIPMENT_SELL_PRICE_RATIO));
}
/**
 * Markup applied to the station SELL value to derive a suggested player
 * marketplace listing price. The intent is "you'd lose 40% selling at a
 * station, so list a bit above that and pocket the difference if a buyer
 * bites." Sellers can override the suggestion freely — this is just the
 * default value the UI prefills.
 */
export const MARKETPLACE_SUGGESTED_MARKUP = 1.5;
/** Lower / upper hard limits for any listing price. */
export const MARKETPLACE_MIN_PRICE = 1;
export const MARKETPLACE_MAX_PRICE = 10_000_000;
/**
 * Marketplace listing TTL. Listings auto-expire after this many ms and
 * the escrowed items are returned to the seller's persisted inventory.
 */
export const MARKETPLACE_LISTING_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * Per-pilot cap on simultaneous active listings. Prevents one player
 * from blanketing the listings UI with hundreds of micro-stacks.
 */
export const MARKETPLACE_MAX_LISTINGS_PER_PILOT = 25;
/**
 * Compute a suggested per-unit marketplace price from a base value
 * (resource baseValue or equipment creditCost). Returns at least
 * `MARKETPLACE_MIN_PRICE`.
 */
export function suggestedMarketUnitPrice(baseValue) {
    if (!Number.isFinite(baseValue) || baseValue <= 0)
        return MARKETPLACE_MIN_PRICE;
    return Math.max(MARKETPLACE_MIN_PRICE, Math.round(baseValue * MARKETPLACE_SUGGESTED_MARKUP));
}
export const RESOURCE_INFO = {
    // ─── Core tier ───
    [ResourceTier.ScrapMetal]: {
        name: 'Scrap Metal', nameEs: 'Chatarra',
        color: 0xcccccc, rarity: 1, baseValue: 5, minSectorDistance: 0,
    },
    [ResourceTier.CrystalShards]: {
        name: 'Crystal Shards', nameEs: 'Fragmentos de Cristal',
        color: 0x4488ff, rarity: 0.4, baseValue: 15, minSectorDistance: 3,
    },
    [ResourceTier.PlasmaCore]: {
        name: 'Plasma Core', nameEs: 'Núcleo de Plasma',
        color: 0xaa44ff, rarity: 0.15, baseValue: 50, minSectorDistance: 8,
    },
    [ResourceTier.VoidEssence]: {
        name: 'Void Essence', nameEs: 'Esencia del Vacío',
        color: 0xff2244, rarity: 0.05, baseValue: 200, minSectorDistance: 15,
    },
    [ResourceTier.DarkMatter]: {
        name: 'Dark Matter', nameEs: 'Materia Oscura',
        color: 0x111133, rarity: 0.015, baseValue: 800, minSectorDistance: 30,
    },
    [ResourceTier.QuantumFragment]: {
        name: 'Quantum Fragment', nameEs: 'Fragmento Cuántico',
        color: 0xff88ff, rarity: 0.003, baseValue: 5000, minSectorDistance: 50,
    },
    // ─── Common rim metals (near origin) ───
    [ResourceTier.IronOre]: {
        name: 'Iron Ore', nameEs: 'Mineral de Hierro',
        color: 0x8b6a4b, rarity: 0.9, baseValue: 8, minSectorDistance: 0,
    },
    [ResourceTier.CopperOre]: {
        name: 'Copper Ore', nameEs: 'Mineral de Cobre',
        color: 0xb87333, rarity: 0.75, baseValue: 12, minSectorDistance: 1,
    },
    [ResourceTier.Silicate]: {
        name: 'Silicate', nameEs: 'Silicato',
        color: 0xdedede, rarity: 0.7, baseValue: 18, minSectorDistance: 2,
    },
    // ─── Mid-range alloys / exotics ───
    [ResourceTier.TitaniumAlloy]: {
        name: 'Titanium Alloy', nameEs: 'Aleación de Titanio',
        color: 0xc0c0d8, rarity: 0.25, baseValue: 80, minSectorDistance: 6,
    },
    [ResourceTier.SolarPlasma]: {
        name: 'Solar Plasma', nameEs: 'Plasma Solar',
        color: 0xffaa22, rarity: 0.18, baseValue: 120, minSectorDistance: 10,
    },
    [ResourceTier.Bioflux]: {
        name: 'Bioflux', nameEs: 'Bioflujo',
        color: 0x44ee88, rarity: 0.12, baseValue: 180, minSectorDistance: 12,
    },
    [ResourceTier.NeutroniumDust]: {
        name: 'Neutronium Dust', nameEs: 'Polvo de Neutronio',
        color: 0x9999bb, rarity: 0.08, baseValue: 320, minSectorDistance: 18,
    },
    // ─── Rare deep-space exotics ───
    [ResourceTier.ChronoQuartz]: {
        name: 'Chrono-Quartz', nameEs: 'Cuarzo Crónico',
        color: 0x88ffcc, rarity: 0.04, baseValue: 600, minSectorDistance: 24,
    },
    [ResourceTier.VoidPearl]: {
        name: 'Void Pearl', nameEs: 'Perla del Vacío',
        color: 0x6644aa, rarity: 0.03, baseValue: 900, minSectorDistance: 32,
    },
    [ResourceTier.PhasePrism]: {
        name: 'Phase Prism', nameEs: 'Prisma de Fase',
        color: 0xff88ee, rarity: 0.02, baseValue: 1400, minSectorDistance: 40,
    },
    // ─── Extreme rarity (deep void) ───
    [ResourceTier.StarforgeIngot]: {
        name: 'Starforge Ingot', nameEs: 'Lingote Forja Estelar',
        color: 0xffdd66, rarity: 0.008, baseValue: 2500, minSectorDistance: 55,
    },
    [ResourceTier.EchoCrystal]: {
        name: 'Echo Crystal', nameEs: 'Cristal de Eco',
        color: 0xaaffff, rarity: 0.005, baseValue: 3800, minSectorDistance: 65,
    },
    [ResourceTier.SingularityShard]: {
        name: 'Singularity Shard', nameEs: 'Fragmento de Singularidad',
        color: 0x2200aa, rarity: 0.003, baseValue: 6500, minSectorDistance: 80,
    },
    [ResourceTier.AbyssalTear]: {
        name: 'Abyssal Tear', nameEs: 'Lágrima Abisal',
        color: 0x220044, rarity: 0.0015, baseValue: 9000, minSectorDistance: 95,
    },
    [ResourceTier.EtherealEssence]: {
        name: 'Ethereal Essence', nameEs: 'Esencia Etérea',
        color: 0xddbbff, rarity: 0.0008, baseValue: 14000, minSectorDistance: 110,
    },
    // Phase 3.1 — surface fauna drop. Origin-tier value: cheap to sell
    // raw, valuable as a Refinery crafting reagent.
    [ResourceTier.OrganicMatter]: {
        name: 'Organic Matter', nameEs: 'Materia Orgánica',
        color: 0x44aa55, rarity: 0.0, baseValue: 22, minSectorDistance: 0,
    },
};
/** All resource tiers in canonical order. */
export const ALL_RESOURCE_TIERS = [
    ResourceTier.ScrapMetal,
    ResourceTier.IronOre,
    ResourceTier.CopperOre,
    ResourceTier.Silicate,
    ResourceTier.CrystalShards,
    ResourceTier.TitaniumAlloy,
    ResourceTier.PlasmaCore,
    ResourceTier.SolarPlasma,
    ResourceTier.Bioflux,
    ResourceTier.VoidEssence,
    ResourceTier.NeutroniumDust,
    ResourceTier.ChronoQuartz,
    ResourceTier.DarkMatter,
    ResourceTier.VoidPearl,
    ResourceTier.PhasePrism,
    ResourceTier.QuantumFragment,
    ResourceTier.StarforgeIngot,
    ResourceTier.EchoCrystal,
    ResourceTier.SingularityShard,
    ResourceTier.AbyssalTear,
    ResourceTier.EtherealEssence,
    ResourceTier.OrganicMatter,
];
/**
 * Difficulty step — every `SECTOR_DIFFICULTY_STEP` sectors away from the
 * origin the asteroid HP, NPC level floor, and spawn counts all scale up.
 * Keeping this shared lets the client and server use the same curve.
 */
export const SECTOR_DIFFICULTY_STEP = 3;
/** Smooth difficulty multiplier that grows every step-radius. */
export function sectorDifficultyMultiplier(sectorDist) {
    return 1 + Math.max(0, sectorDist) / SECTOR_DIFFICULTY_STEP * 0.5;
}
/** Discrete difficulty tier (0..N) used for gating spawn variety. */
export function sectorDifficultyTier(sectorDist) {
    return Math.floor(Math.max(0, sectorDist) / SECTOR_DIFFICULTY_STEP);
}
/**
 * Asteroid HP curve. Rarer resources and deeper sectors produce tougher
 * asteroids. Size still dominates, but higher-tier ores now require real
 * firepower even in small nodes.
 */
export function asteroidHpFor(size, tier, sectorDist) {
    const base = ASTEROID_HP[size] ?? 20;
    const info = RESOURCE_INFO[tier];
    // Rarity in [0..1]; invert for a multiplier in [1..~3.5].
    const rarity = Math.max(0.001, info?.rarity ?? 1);
    const tierMul = 1 + Math.min(2.5, (1 / rarity - 1) * 0.05);
    const distMul = sectorDifficultyMultiplier(sectorDist);
    return Math.round(base * tierMul * distMul);
}
export const ASTEROID_HP = {
    [AsteroidSize.Small]: 20,
    [AsteroidSize.Medium]: 60,
    [AsteroidSize.Large]: 150,
    [AsteroidSize.Huge]: 400,
};
export const ASTEROID_RESOURCE_YIELD = {
    [AsteroidSize.Small]: { min: 1, max: 3 },
    [AsteroidSize.Medium]: { min: 3, max: 8 },
    [AsteroidSize.Large]: { min: 8, max: 20 },
    [AsteroidSize.Huge]: { min: 20, max: 50 },
};
export const DEFAULT_SHIP_CLASS_TRAITS = {
    hullMul: 1,
    shieldMul: 1,
    thrustMul: 1,
    turnRateMul: 1,
    energyMul: 1,
    weaponSlots: 1,
    cargoSlots: 8,
    requiredLevel: 1,
    creditCost: 0,
};
export const SHIP_CLASS_TRAITS = {
    // Fast, fragile recon. Trades hull/shield for thrust/turn.
    [ShipClass.Scout]: {
        hullMul: 0.80, shieldMul: 0.85, thrustMul: 1.25, turnRateMul: 1.30,
        energyMul: 1.10, weaponSlots: 1, cargoSlots: 10,
        requiredLevel: 1, creditCost: 0,
    },
    // Balanced baseline. All muls = 1.0.
    [ShipClass.Fighter]: {
        hullMul: 1.00, shieldMul: 1.00, thrustMul: 1.00, turnRateMul: 1.00,
        energyMul: 1.00, weaponSlots: 2, cargoSlots: 8,
        requiredLevel: 5, creditCost: 5000,
    },
    // Glass-cannon hit-and-run. Stronger offset than Scout on thrust;
    // trades even more hull for it.
    [ShipClass.Interceptor]: {
        hullMul: 0.70, shieldMul: 0.80, thrustMul: 1.35, turnRateMul: 1.35,
        energyMul: 1.20, weaponSlots: 2, cargoSlots: 4,
        requiredLevel: 15, creditCost: 20000,
    },
    // Massive hauler. Deep cargo, slow and unmaneuverable.
    [ShipClass.Freighter]: {
        hullMul: 1.25, shieldMul: 1.15, thrustMul: 0.70, turnRateMul: 0.65,
        energyMul: 0.85, weaponSlots: 1, cargoSlots: 100,
        requiredLevel: 10, creditCost: 15000,
    },
    // Heavy frontline. More guns, tanky, sluggish.
    [ShipClass.Cruiser]: {
        hullMul: 1.30, shieldMul: 1.20, thrustMul: 0.80, turnRateMul: 0.75,
        energyMul: 1.10, weaponSlots: 3, cargoSlots: 20,
        requiredLevel: 30, creditCost: 100000,
    },
    // Top-tier gunship. Biggest non-mothership hull, sluggish turret.
    [ShipClass.Destroyer]: {
        hullMul: 1.50, shieldMul: 1.35, thrustMul: 0.65, turnRateMul: 0.55,
        energyMul: 1.25, weaponSlots: 4, cargoSlots: 30,
        requiredLevel: 70, creditCost: 500000,
    },
    // PvE-only capital ship. Balance here is ignored because players don't
    // pilot one — kept in the table for NPC motherships and admin spawns.
    [ShipClass.Mothership]: {
        hullMul: 10, shieldMul: 10, thrustMul: 0.30, turnRateMul: 0.25,
        energyMul: 3.00, weaponSlots: 6, cargoSlots: 200,
        requiredLevel: 99, creditCost: 0,
    },
};
/** Safe lookup; unknown classes fall back to the Fighter baseline. */
export function getShipClassTraits(shipClass) {
    if (shipClass === undefined || shipClass === null) {
        return SHIP_CLASS_TRAITS[ShipClass.Fighter];
    }
    return SHIP_CLASS_TRAITS[shipClass]
        ?? SHIP_CLASS_TRAITS[ShipClass.Fighter];
}
/**
 * Visual radius scale (world units) per ship class. The mesh built by
 * the client's ship-builder is normalised to roughly a 2-unit bounding
 * box, so multiplying by this scale gives the real on-screen size of
 * the ship. The server also uses this to compute projectile hit radii
 * so that "what you see is what you hit" — wing-tip and engine hits
 * register exactly like nose hits.
 *
 * Keep this in sync with the client's `SHIP_CLASS_SCALES` map in
 * `packages/client/src/game/entity-renderer.ts`. The class keys cover
 * every NPC ship class that can spawn on the server (`scout`, `fighter`,
 * `corvette`, `cruiser`, `destroyer`, `freighter`, `mothership`) plus
 * the additional player-facing classes the hangar can sell.
 */
export const SHIP_CLASS_VISUAL_SCALE = {
    scout: 2.2,
    fighter: 3.2,
    interceptor: 3.2,
    corvette: 5.5,
    frigate: 7.5,
    miner: 5,
    freighter: 9,
    destroyer: 11,
    cruiser: 13,
    battleship: 18,
    carrier: 22,
    mothership: 48,
    // ─── Expanded NPC roster ─────────────────────────────────────────
    // Sized so they slot into the existing tier curve: probe < scout,
    // gunboat ~ fighter, gunship ~ corvette, bomber ~ frigate, etc.
    // Dreadnought is the biggest non-mothership hull.
    probe: 1.6,
    gunboat: 3.4,
    gunship: 5.0,
    bomber: 7.0,
    assault: 5.5,
    support: 5.0,
    stealth: 3.6,
    vanguard: 9,
    drone_carrier: 16,
    dreadnought: 28,
};
/**
 * Projectile hit-test sphere radius (world units) per ship class.
 * Roughly the ship's longest visual half-extent (visual scale × 1.6),
 * with a 6-unit floor so the smallest hulls (probe, scout, fighter,
 * interceptor, gunboat, stealth) still have a fair hitbox at long
 * range. The previous 4-unit floor made tier-1 hulls almost
 * un-hittable in practice — a 4-unit sphere at ~600 units of range
 * is a sub-degree visual angle, which is well below the noise floor
 * of mouse aim. Bumping the floor to 6 widens the hit window on
 * those small hulls without touching anything corvette-sized or
 * larger (their `scale * 1.6` already exceeds the floor).
 *
 * PvP parity: the floor applies to whichever pilot is flying the
 * small hull, so it never gives one race or build an asymmetric
 * advantage. See AGENTS.md §5.
 *
 * Used by the server's ray-vs-sphere test in
 * `WorldManager.processProjectiles`. Keeping it driven by the same
 * visual scale guarantees that hits on wings/engines register exactly
 * the way the player perceives them.
 */
export const SHIP_CLASS_HIT_RADIUS = Object.fromEntries(Object.entries(SHIP_CLASS_VISUAL_SCALE).map(([k, scale]) => [
    k,
    Math.max(scale * 1.6, 6),
]));
/**
 * Chase-camera framing constants (ship-local space, units = world units
 * at the {@link CHASE_REFERENCE_SHIP_SCALE} hull size). The client's
 * {@link CameraSystem} is the canonical consumer; the server imports
 * the same constants so projectile aim can be calibrated against the
 * exact same crosshair the pilot sees on screen.
 *
 * Larger hulls scale both vectors linearly by `shipScale /
 * CHASE_REFERENCE_SHIP_SCALE`, so the camera's downward angle stays
 * constant across every ship class — that's why the firing bias
 * derived below is a single scalar and not a per-class table.
 */
export const CHASE_CAMERA_OFFSET = { x: 0, y: 4.6, z: 19 };
export const CHASE_CAMERA_LOOK_AHEAD = { x: 0, y: 3.6, z: -34 };
export const CHASE_REFERENCE_SHIP_SCALE = 3.2;
/**
 * Nominal target distance (world units, fighter-scale) at which the
 * player's projectiles are calibrated to cross the chase-camera's
 * center-screen ray ("zeroing range"). Picked for typical close-range
 * dogfighting; outside this range the bullet's vertical offset stays
 * well within the smallest hit radius (6u floor in
 * {@link SHIP_CLASS_HIT_RADIUS}), so beacons / asteroids / fighters
 * still get hit at 50–250u with no visible aim correction needed.
 *
 * Larger hulls inherit a proportionally longer convergence range
 * (Cruiser ≈ 4× Fighter), which mirrors the longer effective combat
 * distance those ships engage at.
 */
export const PLAYER_FIRING_CONVERGENCE_RANGE = 100;
/**
 * Upward pitch bias (radians) applied to the player's projectile spawn
 * direction so bullets cross the chase-camera's center-screen ray at
 * {@link PLAYER_FIRING_CONVERGENCE_RANGE}.
 *
 * Why we need it: the chase camera sits {@link CHASE_CAMERA_OFFSET}.y
 * units above the ship origin and looks slightly down toward
 * {@link CHASE_CAMERA_LOOK_AHEAD}. The reticle (screen center)
 * therefore points to a world position with positive Y at any finite
 * range, while a bullet fired straight along the ship's nose travels
 * at Y=0 forever. Without correction the bullet visibly impacts
 * BELOW the crosshair at typical engagement distances (~2u low at
 * 100u of range, growing with proximity), forcing the pilot to
 * compensate by aiming above their target.
 *
 * The bias is invariant to ship visual scale because the camera
 * offset, look-ahead, and convergence range all scale linearly with
 * the hull. NPCs use a separate firing path
 * (`combat-system.fireWeapon`) and are unaffected.
 */
export const PLAYER_FIRING_PITCH_BIAS_RAD = (() => {
    const cy = CHASE_CAMERA_OFFSET.y;
    const cz = CHASE_CAMERA_OFFSET.z;
    const ly = CHASE_CAMERA_LOOK_AHEAD.y;
    const lz = CHASE_CAMERA_LOOK_AHEAD.z;
    const fwdLen = Math.hypot(ly - cy, lz - cz);
    const fwdY = (ly - cy) / fwdLen;
    const fwdZ = (lz - cz) / fwdLen;
    const R = PLAYER_FIRING_CONVERGENCE_RANGE;
    // Camera-ray distance at which it crosses world z = -R (ship-local).
    const d = -(R + cz) / fwdZ;
    // Camera-ray Y at the convergence plane.
    const targetY = cy + d * fwdY;
    return Math.atan2(targetY, R);
})();
/**
 * Map a {@link ShipClass} enum value to its lowercase string key, the
 * form used by NPC templates, the ship-builder, and the visual / hit
 * radius lookup tables above.
 */
export const SHIP_CLASS_KEY = {
    [ShipClass.Scout]: 'scout',
    [ShipClass.Fighter]: 'fighter',
    [ShipClass.Interceptor]: 'interceptor',
    [ShipClass.Cruiser]: 'cruiser',
    [ShipClass.Freighter]: 'freighter',
    [ShipClass.Destroyer]: 'destroyer',
    [ShipClass.Mothership]: 'mothership',
};
export const SHIP_STATS = {
    [ShipClass.Scout]: {
        name: 'Scout', nameEs: 'Explorador',
        baseHp: 80, baseShield: 43, baseEnergy: 110, baseSpeed: 125,
        baseTurnRate: 1.30, cargoSlots: 10, weaponSlots: 1, requiredLevel: 1, creditCost: 0,
    },
    [ShipClass.Fighter]: {
        name: 'Fighter', nameEs: 'Caza',
        baseHp: 100, baseShield: 50, baseEnergy: 100, baseSpeed: 100,
        baseTurnRate: 1.00, cargoSlots: 8, weaponSlots: 2, requiredLevel: 5, creditCost: 5000,
    },
    [ShipClass.Interceptor]: {
        name: 'Interceptor', nameEs: 'Interceptor',
        baseHp: 70, baseShield: 40, baseEnergy: 120, baseSpeed: 135,
        baseTurnRate: 1.35, cargoSlots: 4, weaponSlots: 2, requiredLevel: 15, creditCost: 20000,
    },
    [ShipClass.Cruiser]: {
        name: 'Cruiser', nameEs: 'Crucero',
        baseHp: 130, baseShield: 60, baseEnergy: 110, baseSpeed: 80,
        baseTurnRate: 0.75, cargoSlots: 20, weaponSlots: 3, requiredLevel: 30, creditCost: 100000,
    },
    [ShipClass.Freighter]: {
        name: 'Freighter', nameEs: 'Carguero',
        baseHp: 125, baseShield: 58, baseEnergy: 85, baseSpeed: 70,
        baseTurnRate: 0.65, cargoSlots: 100, weaponSlots: 1, requiredLevel: 10, creditCost: 15000,
    },
    [ShipClass.Destroyer]: {
        name: 'Destroyer', nameEs: 'Destructor',
        baseHp: 150, baseShield: 68, baseEnergy: 125, baseSpeed: 65,
        baseTurnRate: 0.55, cargoSlots: 30, weaponSlots: 4, requiredLevel: 70, creditCost: 500000,
    },
    [ShipClass.Mothership]: {
        name: 'Mothership', nameEs: 'Nave Nodriza',
        baseHp: 1000, baseShield: 500, baseEnergy: 300, baseSpeed: 30,
        baseTurnRate: 0.25, cargoSlots: 200, weaponSlots: 6, requiredLevel: 99, creditCost: 0,
    },
};
/**
 * Human-readable bullet list describing a ship class' trade-offs. Mirrors
 * {@link formatRaceTraitSummary} — only non-neutral (≠ 1) values appear,
 * so Fighter (the baseline) returns an empty array.
 */
export function formatShipTraitSummary(shipClass, locale = 'en') {
    const t = getShipClassTraits(shipClass);
    const lines = [];
    const pct = (v) => {
        const delta = Math.round((v - 1) * 100);
        return `${delta >= 0 ? '+' : ''}${delta}%`;
    };
    // Picks the matching string for the requested locale; falls back to
    // English for unknown locales so a fresh language pack still surfaces
    // a readable trait summary even before translators fill these in.
    const push = (en, es, pt) => {
        if (locale === 'es')
            lines.push(es);
        else if (locale === 'pt')
            lines.push(pt);
        else
            lines.push(en);
    };
    if (t.hullMul !== 1 || t.shieldMul !== 1) {
        push(`${pct(t.hullMul)} hull / ${pct(t.shieldMul)} shield`, `${pct(t.hullMul)} casco / ${pct(t.shieldMul)} escudo`, `${pct(t.hullMul)} casco / ${pct(t.shieldMul)} escudo`);
    }
    if (t.thrustMul !== 1 || t.turnRateMul !== 1) {
        push(`${pct(t.thrustMul)} thrust / ${pct(t.turnRateMul)} turn`, `${pct(t.thrustMul)} empuje / ${pct(t.turnRateMul)} giro`, `${pct(t.thrustMul)} impulso / ${pct(t.turnRateMul)} curva`);
    }
    if (t.energyMul !== 1) {
        push(`${pct(t.energyMul)} energy capacity`, `${pct(t.energyMul)} capacidad de energía`, `${pct(t.energyMul)} capacidade de energia`);
    }
    return lines;
}
/**
 * Tunables for the rare NPC "motherships". These are massive capital ships
 * meant to be taken down by fleets of high-level players. They spawn rarely
 * in deeper sectors, move slowly, never collide with celestial bodies, and
 * reward every contributing player with XP and a cache of resources.
 */
export const MOTHERSHIP_CONFIG = {
    /** Minimum sector distance before a mothership can even be considered. */
    minSectorDistance: 12,
    /** Probability of a mothership existing in a qualifying sector. */
    spawnChance: 0.04,
    /** How long after destruction before a new mothership respawns (ms). */
    respawnDelayMs: 45 * 60_000,
    /** Obstacle avoidance radius in world units — huge so they never crash. */
    avoidRadius: 350,
    /** Visual scale multiplier applied to rendered geometry. */
    visualScale: 8,
    /** XP pool awarded when destroyed (distributed by damage contribution). */
    baseXpReward: 5000,
    /** Number of distinct resource tiers dropped from the cache. */
    lootTierCount: 4,
    /** Amount range dropped per tier. */
    lootAmountPerTier: { min: 8, max: 28 },
    /** Base damage per weapon hit. */
    baseDamage: 18,
    /** Cooldown between primary weapon shots (ms). */
    fireCooldownMs: 1600,
    /** Aggro/detection range — they engage anyone within this radius. */
    aggroRange: 900,
};
export const RACE_INFO = {
    [RaceId.Terran]: {
        name: 'Terran', nameEs: 'Terrícola',
        description: 'Balanced explorers from Earth', descriptionEs: 'Exploradores equilibrados de la Tierra',
        bonusType: 'xp', bonusValue: 0.10,
        disposition: 'neutral', primaryColor: 0x4488cc, secondaryColor: 0xaaccee,
        unlockLevel: 0, premium: false,
    },
    [RaceId.Zyrkath]: {
        name: "Zyr'kath", nameEs: "Zyr'kath",
        description: 'Aggressive insectoid swarm', descriptionEs: 'Enjambre insectoide agresivo',
        bonusType: 'pveDamage', bonusValue: 0.15,
        // Acid-chitin yellow-green: aggressive, "wasp / hornet" reading,
        // distinct from Sylphari's softer jade.
        disposition: 'hostile', primaryColor: 0x7c9c10, secondaryColor: 0xddff33,
        unlockLevel: 0, premium: false,
    },
    [RaceId.Luminara]: {
        name: 'Luminara', nameEs: 'Luminara',
        description: 'Peaceful crystalline energy beings', descriptionEs: 'Seres de energía cristalina pacíficos',
        bonusType: 'shieldRegen', bonusValue: 0.25,
        disposition: 'friendly', primaryColor: 0x88ccff, secondaryColor: 0xccffff,
        unlockLevel: 0, premium: false,
    },
    [RaceId.Vokren]: {
        name: "Vok'ren", nameEs: "Vok'ren",
        description: 'Nomadic traders and merchants', descriptionEs: 'Comerciantes y mercaderes nómadas',
        bonusType: 'mining', bonusValue: 0.25,
        disposition: 'neutral', primaryColor: 0xcc8844, secondaryColor: 0xffcc88,
        unlockLevel: 0, premium: true,
    },
    [RaceId.ObsidianCollective]: {
        name: 'Obsidian Collective', nameEs: 'Colectivo Obsidiana',
        description: 'Logical machine intelligence', descriptionEs: 'Inteligencia de máquina lógica',
        bonusType: 'pickupRadius', bonusValue: 0.20,
        disposition: 'neutral', primaryColor: 0x333344, secondaryColor: 0x666688,
        unlockLevel: 0, premium: true,
    },
    [RaceId.VoidWalker]: {
        name: 'Void Walker', nameEs: 'Caminante del Vacío',
        description: 'Mysterious ancient race', descriptionEs: 'Raza antigua y misteriosa',
        bonusType: 'versatile', bonusValue: 0.10,
        // Dark indigo hull with electric magenta-violet runes: keeps the
        // "ancient and mysterious" tone but is unmistakable next to the
        // Abyssal Chorus' bioluminescent teal palette.
        disposition: 'unknown', primaryColor: 0x2a0d3a, secondaryColor: 0xc070ff,
        unlockLevel: 50, premium: true,
    },
    // ─── Frontier civilizations (deeper sectors, larger ships) ───
    [RaceId.Sylphari]: {
        name: 'Sylphari', nameEs: 'Sylphari',
        description: 'Plant-based bio-architects with living carriers',
        descriptionEs: 'Bio-arquitectos vegetales con portanaves vivientes',
        bonusType: 'stationRepair', bonusValue: 0.30,
        // Soft forest/jade with mint highlights: organic and leafy,
        // distinctly cooler than Zyr'kath's acid yellow-green.
        disposition: 'friendly', primaryColor: 0x2d8b5e, secondaryColor: 0x9be8c0,
        unlockLevel: 20, premium: true,
    },
    [RaceId.KrelgHegemony]: {
        name: 'Krelg Hegemony', nameEs: 'Hegemonía Krelg',
        description: 'Warlord clans piloting industrial dreadnoughts',
        descriptionEs: 'Clanes guerreros pilotando acorazados industriales',
        bonusType: 'pveDamage', bonusValue: 0.35,
        disposition: 'hostile', primaryColor: 0xaa2211, secondaryColor: 0xff6633,
        unlockLevel: 25, premium: true,
    },
    [RaceId.TharixCabal]: {
        name: 'Tharix Cabal', nameEs: 'Cábala Tharix',
        description: 'Silicate infiltrators masters of stealth cruisers',
        descriptionEs: 'Infiltradores silíceos, maestros del sigilo',
        bonusType: 'stealth', bonusValue: 0.25,
        disposition: 'neutral', primaryColor: 0x335577, secondaryColor: 0x66aaff,
        unlockLevel: 30, premium: true,
    },
    [RaceId.ChronosSeers]: {
        name: 'Chronos Seers', nameEs: 'Videntes de Chronos',
        description: 'Time-weavers riding crystalline schooners',
        descriptionEs: 'Tejedores del tiempo en goletas cristalinas',
        bonusType: 'pveFireRate', bonusValue: 0.25,
        disposition: 'neutral', primaryColor: 0x88ffcc, secondaryColor: 0xddffee,
        unlockLevel: 40, premium: true,
    },
    [RaceId.NovaForgers]: {
        name: 'Nova Forgers', nameEs: 'Forjadores Nova',
        description: 'Titans of plasma-forged battleships',
        descriptionEs: 'Titanes de acorazados forjados en plasma',
        bonusType: 'pveDamage', bonusValue: 0.40,
        disposition: 'neutral', primaryColor: 0xff8822, secondaryColor: 0xffcc66,
        unlockLevel: 55, premium: true,
    },
    [RaceId.AbyssalChorus]: {
        name: 'Abyssal Chorus', nameEs: 'Coro Abisal',
        description: 'Deep-void leviathans harmonising reality',
        descriptionEs: 'Leviatanes del vacío profundo que armonizan la realidad',
        bonusType: 'versatile', bonusValue: 0.10,
        // Abyssal blue-black hull with bioluminescent teal/cyan glow:
        // deep-sea leviathan reading, clearly different from Void Walker's
        // indigo + violet palette.
        disposition: 'unknown', primaryColor: 0x041a26, secondaryColor: 0x2effbb,
        unlockLevel: 70, premium: true,
    },
    // ─── Apex civilizations (deep-sector only) ──────────────────────────
    [RaceId.EidolonConclave]: {
        name: 'Eidolon Conclave', nameEs: 'Cónclave Eidolón',
        description: 'Mirror-hulled paradox-knights from beyond mortality',
        descriptionEs: 'Caballeros paradoja con cascos espejo, más allá de la mortalidad',
        bonusType: 'versatile', bonusValue: 0.10,
        // Pale mercury-silver primary with cool ethereal blue accents — reads as
        // "ghost / mirror" without colliding with Luminara's warm crystal cyan or
        // Tharix's industrial silicate teal.
        disposition: 'hostile', primaryColor: 0xc0c8d8, secondaryColor: 0x88aaff,
        unlockLevel: 100, premium: true,
    },
    [RaceId.AstralLeviathan]: {
        name: 'Astralleviathan', nameEs: 'Leviatán Astral',
        description: 'Singing inter-galactic beasts piloting living capital ships',
        descriptionEs: 'Bestias intergalácticas cantantes pilotando naves capitales vivientes',
        bonusType: 'versatile', bonusValue: 0.10,
        // Deep midnight purple primary with pale gold star-flecks — reads as
        // "moving constellation" and is unmistakable next to Abyssal Chorus
        // (teal-on-black) or Void Walker (indigo + violet).
        disposition: 'unknown', primaryColor: 0x150a3a, secondaryColor: 0xffd47a,
        unlockLevel: 200, premium: true,
    },
    [RaceId.SingularitySovereign]: {
        name: 'Singularity Sovereigns', nameEs: 'Soberanos de Singularidad',
        description: 'Event-horizon emperors who pilot weaponised micro-black-holes',
        descriptionEs: 'Emperadores del horizonte de sucesos que pilotan micro-agujeros negros',
        bonusType: 'pveDamage', bonusValue: 0.50,
        // Pure black hull with violent crimson event-horizon glow — every
        // other apex tint is cool, so a hot red-on-black makes the
        // Sovereigns instantly recognisable as "do not engage solo".
        disposition: 'hostile', primaryColor: 0x000000, secondaryColor: 0xff2244,
        unlockLevel: 400, premium: true,
    },
};
// ─── Premium roster helpers ───────────────────────────────────────────
//
// Free pilots can pick from {Terran, Zyr'kath, Luminara}. Every other
// race is a Premium-subscription perk. The roster split is balanced so
// the free three already cover the core gameplay archetypes (XP +
// economy, PvE damage, shield regen / tank), and the premium roster
// adds *flavour* and slightly stronger PvE/economy tilts — the
// `RACE_TRAITS` table guarantees PvP parity regardless of tier.
/** Race ids that any account can pick at character creation. */
export const FREE_RACE_IDS = Object.entries(RACE_INFO)
    .filter(([, info]) => !info.premium)
    .map(([id]) => Number(id));
/** Race ids that require an active Premium subscription to select. */
export const PREMIUM_RACE_IDS = Object.entries(RACE_INFO)
    .filter(([, info]) => info.premium)
    .map(([id]) => Number(id));
/** Quick predicate so the server, client UI, and tests stay in sync. */
export function isPremiumRace(raceId) {
    if (raceId === undefined || raceId === null)
        return false;
    return RACE_INFO[raceId]?.premium === true;
}
// ─── UI color helpers ────────────────────────────────────────────────
//
// Several races have intentionally dark hull primaries (Singularity
// Sovereigns is pure black, Astralleviathan is near-black indigo,
// Abyssal Chorus is a deep ocean blue) which read beautifully on the
// rendered ship but become unreadable when reused as a label color on
// the dark in-game UI background. The helpers below pick a brighter
// "label" tint per race so the name stays legible on `rgba(0,8,0,...)`
// surfaces without changing the underlying livery used by the renderer.
/** Approximate WCAG-style perceived luminance of an RRGGBB color (0..1). */
export function colorLuminance(hex) {
    const r = ((hex >> 16) & 0xff) / 255;
    const g = ((hex >> 8) & 0xff) / 255;
    const b = (hex & 0xff) / 255;
    // Standard Rec. 709 weights — same approximation used by the contrast
    // formula in the WCAG 2.x relative-luminance definition. We skip the
    // gamma correction step to keep this branch-free; the resulting ranking
    // is monotonic with the gamma-corrected version, which is all we need
    // for picking between two candidate label colors.
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function lightenColorHex(hex, amount) {
    const a = Math.max(0, Math.min(1, amount));
    const r = (hex >> 16) & 0xff;
    const g = (hex >> 8) & 0xff;
    const b = hex & 0xff;
    const lr = Math.round(r + (255 - r) * a);
    const lg = Math.round(g + (255 - g) * a);
    const lb = Math.round(b + (255 - b) * a);
    return (lr << 16) | (lg << 8) | lb;
}
/**
 * Pick a UI-safe label color for a race. When the race's primary
 * livery is too dark to read on the dark UI background we fall back to
 * the brighter `secondaryColor`, then lighten further if even that is
 * borderline. Never mutates the underlying RACE_INFO entry — this is
 * purely a presentation transform.
 */
export function raceLabelColorHex(raceId) {
    const info = RACE_INFO[raceId];
    if (!info)
        return 0xffffff;
    const primaryLum = colorLuminance(info.primaryColor);
    // Threshold tuned against the actual UI background `rgba(0,8,0,0.7)`:
    // primaries below ~0.35 luminance start losing readability at the
    // ~12px font size used by the race cards.
    if (primaryLum >= 0.35)
        return info.primaryColor;
    const secondaryLum = colorLuminance(info.secondaryColor);
    if (secondaryLum >= 0.45)
        return info.secondaryColor;
    // Both are dark — lighten the secondary until it crosses the floor.
    return lightenColorHex(info.secondaryColor, 0.55);
}
/** CSS `#rrggbb` form of {@link raceLabelColorHex}. */
export function raceLabelColorCss(raceId) {
    return `#${raceLabelColorHex(raceId).toString(16).padStart(6, '0')}`;
}
/**
 * Authoritative gate used by both client UI (greying cards) and server
 * validation (`SaveCharacter`/`JoinGame`). Always returns true for the
 * free roster; only returns true for premium races when the caller's
 * subscription is active.
 */
export function canAccountUseRace(raceId, hasPremium) {
    if (raceId === undefined || raceId === null)
        return false;
    if (!isPremiumRace(raceId))
        return true;
    return hasPremium;
}
// ─── Premium subscription perks ──────────────────────────────────────────
//
// `PREMIUM_PERKS` is the single source of truth for every numeric
// difference between a free account and a Premium subscriber. The
// design intent — kept in sync with the marketing copy — is:
//
//   1. NEVER grant strict PvP advantage. Premium gives convenience,
//      economy, slot capacity, and cosmetics. Combat / movement /
//      damage / shield / hull / weapon-cooldown values are
//      identical for both tiers (race traits in `RACE_TRAITS` remain
//      the only source of per-race PvP-relevant variance).
//   2. NEVER take anything away from a free pilot that they have
//      today. Caps that exist (mission slots, marketplace listings)
//      keep their current value as the FREE floor; Premium adds on
//      top with `*Premium` constants.
//   3. Discounts are CAPPED at 50% so a stacked premium-+-station-
//      owner pilot can never end up with negative-priced items.
//
// Every code site that branches on premium MUST consume these
// constants — never inline a number — so re-balancing the offer is
// a one-file change.
export const PREMIUM_PERKS = {
    /**
     * Multiplier applied to `warpTravelCost(...)` when the warping
     * pilot's account is Premium. 0.75 = "Premium pays 25% less per
     * jump" — surfaces immediately on the very first warp gate use,
     * which is one of the highest-touch credit sinks in the game.
     */
    warpCostMul: 0.75,
    /**
     * Extra fraction off station shop prices for Premium accounts.
     * Stacks with `OWNER_SHOP_DISCOUNT` (10%) when the buyer also owns
     * the sector — a Premium owner pays `1 - 0.10 - 0.05 = 0.85` of
     * the base price. Clamped server-side to never exceed 50% total
     * discount so promo-stacking can't break the economy.
     */
    stationShopDiscount: 0.05,
    /**
     * Hard ceiling on the combined station-shop discount (Premium +
     * sector ownership + any future discount sources). Prevents a
     * stacking bug from ever pushing prices to ≤ 0.
     */
    stationShopMaxDiscount: 0.50,
    /**
     * Marketplace active-listing cap for Premium accounts. Free pilots
     * keep `MARKETPLACE_MAX_LISTINGS_PER_PILOT`; Premium gets to keep
     * roughly twice as many simultaneous listings live, which is the
     * single biggest QoL bump for active traders.
     */
    marketplaceListingsCap: 50,
    /**
     * Active-mission cap for Premium accounts. Free pilots keep
     * `MAX_ACTIVE_MISSIONS` (5); Premium can juggle a couple of
     * additional chains in parallel.
     */
    activeMissionsCap: 8,
    /**
     * Credits granted to the active character on the FIRST login of
     * each UTC day for a Premium account. Pure retention hook — the
     * amount is small enough that it doesn't trivialize the warp-gate
     * economy but visible enough that "I should log in today" lands.
     */
    dailyLoginCredits: 1_000,
    /**
     * Global XP multiplier for Premium accounts, applied on every
     * `awardXP` call (kills, mining, missions, crafting, bounties) on
     * top of the race trait `xpMul`. This is a TIME advantage, not a
     * build advantage: two pilots of equal level remain identical in
     * PvP — Premium simply reaches a given level sooner. Keeps the
     * RACE_TRAITS parity invariant intact (level stays the only
     * PvP-visible progression axis, attainable by both tiers).
     */
    xpMul: 1.5,
    /**
     * Credit multiplier applied to combat payouts (NPC kill drops,
     * mission credit rewards, bounty payouts) for Premium accounts.
     * Mirrors `xpMul`'s "progress faster" framing on the economy side.
     * Does NOT apply to mining sale prices (race traits own that knob)
     * or P2P trades (would distort the player market).
     */
    killCreditsMul: 1.25,
};
/**
 * Credits granted to a NON-premium pilot the first time they log in each
 * UTC day — a lightweight "reason to come back" hook. Deliberately well
 * below {@link PREMIUM_PERKS.dailyLoginCredits} (1,000) so Premium keeps a
 * clear daily edge, but enough that a returning free pilot feels rewarded.
 * Credits are economy currency available to every race/tier, so this does
 * not affect PvP parity (see AGENTS.md §5).
 */
export const FREE_DAILY_LOGIN_CREDITS = 250;
/**
 * Maximum 3D sector distance a FREE pilot can cover in a single warp
 * jump. Longer journeys remain possible by chaining hops gate-to-gate
 * (paying each leg), so the whole universe stays reachable for free
 * accounts — just slower. Premium pilots have no range limit and can
 * jump straight to any discovered gate sector. Enforced server-side
 * in the WarpGateUse handler; mirrored in the client warp UI so far
 * destinations render with a Premium lock instead of failing on use.
 */
export const FREE_WARP_MAX_JUMP = 6;
/**
 * Whether a pilot may warp directly from `from` to `to` given their
 * premium status. Premium = always; free = only within
 * {@link FREE_WARP_MAX_JUMP} sectors (3D euclidean). Single source of
 * truth consumed by the server WarpGateUse handler and the client
 * warp UI lock, so the two can never disagree.
 */
export function isWarpJumpAllowed(from, to, hasPremium) {
    if (hasPremium)
        return true;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz) <= FREE_WARP_MAX_JUMP;
}
/**
 * Sector distance (3D, from origin) beyond which planets owned by a
 * premium race require a Premium account to LAND on. Orbiting,
 * mining the planet's asteroid belts, and fighting its patrols stay
 * free for everyone — only the surface (landing, surface missions,
 * surface resources) is gated. Inside this radius every planet is
 * landable by all pilots so the early/mid game is never paywalled.
 */
export const PREMIUM_PLANET_MIN_DIST = 25;
/** Whether landing on a planet requires Premium, given its race and sector distance. */
export function isPremiumPlanet(raceId, sectorDist) {
    return isPremiumRace(raceId) && sectorDist >= PREMIUM_PLANET_MIN_DIST;
}
/**
 * Combined XP multiplier for an award: race trait `xpMul` × Premium
 * `PREMIUM_PERKS.xpMul`. The server's `awardXP` is the only consumer
 * on the hot path; exposed here so the composition is unit-testable
 * and the client can mirror the math in UI copy if needed.
 */
export function xpMultiplierFor(raceId, hasPremium) {
    return getRaceTraits(raceId).xpMul * (hasPremium ? PREMIUM_PERKS.xpMul : 1);
}
/**
 * Credit payout for combat-economy rewards (mission credits, bounty
 * payouts) after the Premium boost. Free accounts get the base amount
 * unchanged; Premium gets `PREMIUM_PERKS.killCreditsMul`, rounded.
 */
export function creditPayoutFor(amount, hasPremium) {
    return hasPremium ? Math.round(amount * PREMIUM_PERKS.killCreditsMul) : amount;
}
/**
 * Effective warp travel cost taking into account the Premium
 * discount. Drop-in replacement for `warpTravelCost` in code paths
 * that have a premium flag handy. Falls back to the un-discounted
 * value when `hasPremium` is false so the function is safe to use
 * unconditionally.
 */
export function warpTravelCostFor(from, to, hasPremium) {
    const base = warpTravelCost(from, to);
    if (!hasPremium)
        return base;
    return Math.max(0, Math.ceil(base * PREMIUM_PERKS.warpCostMul));
}
/**
 * Effective station-shop discount fraction for a given buyer. Stacks
 * the sector-owner discount with the Premium discount, then clamps
 * to `PREMIUM_PERKS.stationShopMaxDiscount` so the multiplier
 * applied at checkout (`1 - effectiveDiscount`) is always between
 * `1 - max` and `1`.
 *
 * Returns the *fraction off* (e.g. 0.15 = 15% off), NOT the
 * multiplier — callers should compute `Math.max(1, Math.round(price * (1 - effective)))`.
 */
export function effectiveStationShopDiscount(isOwner, hasPremium) {
    let d = 0;
    if (isOwner)
        d += OWNER_SHOP_DISCOUNT;
    if (hasPremium)
        d += PREMIUM_PERKS.stationShopDiscount;
    return Math.min(d, PREMIUM_PERKS.stationShopMaxDiscount);
}
/** Per-account active-mission cap (Free vs. Premium). */
export function missionsCapFor(hasPremium) {
    return hasPremium ? PREMIUM_PERKS.activeMissionsCap : MAX_ACTIVE_MISSIONS;
}
/** Per-account marketplace active-listing cap (Free vs. Premium). */
export function marketplaceListingsCapFor(hasPremium) {
    return hasPremium
        ? PREMIUM_PERKS.marketplaceListingsCap
        : MARKETPLACE_MAX_LISTINGS_PER_PILOT;
}
export const DEFAULT_RACE_TRAITS = {
    xpMul: 1,
    miningYieldMul: 1,
    sellPriceMul: 1,
    pickupRadiusMul: 1,
    pveDamageMul: 1,
    pveFireRateMul: 1,
    hullMul: 1,
    shieldMul: 1,
    shieldRegenRateMul: 1,
    thrustMul: 1,
    turnRateMul: 1,
    stationRepairMul: 1,
    stealthMul: 1,
};
function traits(partial) {
    return { ...DEFAULT_RACE_TRAITS, ...partial };
}
export const RACE_TRAITS = {
    // Core — everyone above baseline by a single PvE/economy perk.
    [RaceId.Terran]: traits({ xpMul: 1.10 }),
    [RaceId.Zyrkath]: traits({ pveDamageMul: 1.15 }),
    [RaceId.Luminara]: traits({
        shieldRegenRateMul: 1.25,
        shieldMul: 1.05,
        hullMul: 0.95,
    }),
    [RaceId.Vokren]: traits({ miningYieldMul: 1.25, sellPriceMul: 1.10 }),
    [RaceId.ObsidianCollective]: traits({
        pickupRadiusMul: 1.20,
        turnRateMul: 1.05,
        thrustMul: 0.95,
    }),
    [RaceId.VoidWalker]: traits({ xpMul: 1.10, miningYieldMul: 1.10 }),
    // Frontier — same pattern, slightly stronger PvE perks to reward the
    // deeper-sector grind, still PvP-safe because the bonuses are PvE-only.
    [RaceId.Sylphari]: traits({
        stationRepairMul: 1.30,
        hullMul: 1.05,
        shieldMul: 0.95,
    }),
    [RaceId.KrelgHegemony]: traits({
        pveDamageMul: 1.35,
        hullMul: 1.10,
        shieldMul: 0.90,
    }),
    [RaceId.TharixCabal]: traits({ stealthMul: 1.25 }),
    [RaceId.ChronosSeers]: traits({
        pveFireRateMul: 1.25,
        turnRateMul: 1.10,
        thrustMul: 0.90,
    }),
    [RaceId.NovaForgers]: traits({
        pveDamageMul: 1.40,
        hullMul: 1.05,
        shieldMul: 0.95,
    }),
    [RaceId.AbyssalChorus]: traits({
        xpMul: 1.10,
        miningYieldMul: 1.10,
        sellPriceMul: 1.10,
    }),
    // Apex — slightly stronger PvE perks than the frontier band, paired
    // with combat trade-offs so PvP parity stays intact (the bonuses are
    // PvE-only; the trade-offs balance the PvP kit).
    [RaceId.EidolonConclave]: traits({
        pveFireRateMul: 1.30,
        turnRateMul: 1.15,
        thrustMul: 0.85,
    }),
    [RaceId.AstralLeviathan]: traits({
        xpMul: 1.20,
        miningYieldMul: 1.15,
        sellPriceMul: 1.15,
        hullMul: 1.15,
        shieldMul: 0.85,
    }),
    [RaceId.SingularitySovereign]: traits({
        pveDamageMul: 1.50,
        hullMul: 1.10,
        shieldMul: 0.90,
        thrustMul: 0.95,
        turnRateMul: 0.95,
    }),
};
/** Safe lookup that always returns a defined traits record. */
export function getRaceTraits(raceId) {
    if (raceId === undefined || raceId === null)
        return DEFAULT_RACE_TRAITS;
    return RACE_TRAITS[raceId] ?? DEFAULT_RACE_TRAITS;
}
/**
 * Human-readable bullet list describing a race's traits. Used by the
 * character-creation UI so players can see exactly what they're choosing
 * without having to guess at the bonusType keyword.
 *
 * Only non-neutral (≠ 1.0) traits are included; a baseline race returns
 * an empty array.
 */
export function formatRaceTraitSummary(raceId, locale = 'en') {
    const t = getRaceTraits(raceId);
    const lines = [];
    const pct = (v) => {
        const delta = Math.round((v - 1) * 100);
        return `${delta >= 0 ? '+' : ''}${delta}%`;
    };
    // Picks the matching string for the requested locale; falls back to
    // English for unknown locales so trait summaries stay readable even
    // before a runtime-loaded language has its trait copy translated.
    const push = (en, es, pt) => {
        if (locale === 'es')
            lines.push(es);
        else if (locale === 'pt')
            lines.push(pt);
        else
            lines.push(en);
    };
    if (t.xpMul !== 1)
        push(`${pct(t.xpMul)} XP gain`, `${pct(t.xpMul)} ganancia de XP`, `${pct(t.xpMul)} ganho de XP`);
    if (t.miningYieldMul !== 1)
        push(`${pct(t.miningYieldMul)} mining yield`, `${pct(t.miningYieldMul)} minado`, `${pct(t.miningYieldMul)} mineração`);
    if (t.sellPriceMul !== 1)
        push(`${pct(t.sellPriceMul)} sell price`, `${pct(t.sellPriceMul)} precio de venta`, `${pct(t.sellPriceMul)} preço de venda`);
    if (t.pickupRadiusMul !== 1)
        push(`${pct(t.pickupRadiusMul)} loot magnet`, `${pct(t.pickupRadiusMul)} imán de botín`, `${pct(t.pickupRadiusMul)} ímã de espólio`);
    if (t.pveDamageMul !== 1)
        push(`${pct(t.pveDamageMul)} damage vs NPCs (PvE)`, `${pct(t.pveDamageMul)} daño contra NPCs (PvE)`, `${pct(t.pveDamageMul)} dano contra NPCs (PvE)`);
    if (t.pveFireRateMul !== 1)
        push(`${pct(t.pveFireRateMul)} fire rate vs NPCs (PvE)`, `${pct(t.pveFireRateMul)} cadencia contra NPCs (PvE)`, `${pct(t.pveFireRateMul)} cadência contra NPCs (PvE)`);
    if (t.stationRepairMul !== 1)
        push(`${pct(t.stationRepairMul)} station repair rate`, `${pct(t.stationRepairMul)} reparación en estación`, `${pct(t.stationRepairMul)} reparo na estação`);
    if (t.shieldRegenRateMul !== 1)
        push(`${pct(t.shieldRegenRateMul)} shield regen`, `${pct(t.shieldRegenRateMul)} regen. de escudo`, `${pct(t.shieldRegenRateMul)} regen. de escudo`);
    if (t.stealthMul !== 1)
        push(`${pct(t.stealthMul)} stealth (NPC detect)`, `${pct(t.stealthMul)} sigilo (detección NPC)`, `${pct(t.stealthMul)} furtividade (detecção NPC)`);
    if (t.hullMul !== 1 || t.shieldMul !== 1) {
        push(`${pct(t.hullMul)} hull / ${pct(t.shieldMul)} shield`, `${pct(t.hullMul)} casco / ${pct(t.shieldMul)} escudo`, `${pct(t.hullMul)} casco / ${pct(t.shieldMul)} escudo`);
    }
    if (t.thrustMul !== 1 || t.turnRateMul !== 1) {
        push(`${pct(t.thrustMul)} thrust / ${pct(t.turnRateMul)} turn`, `${pct(t.thrustMul)} empuje / ${pct(t.turnRateMul)} giro`, `${pct(t.thrustMul)} impulso / ${pct(t.turnRateMul)} curva`);
    }
    return lines;
}
/** All race ids in spawn/unlock order. */
export const ALL_RACE_IDS = [
    RaceId.Terran,
    RaceId.Zyrkath,
    RaceId.Luminara,
    RaceId.Vokren,
    RaceId.ObsidianCollective,
    RaceId.VoidWalker,
    RaceId.Sylphari,
    RaceId.KrelgHegemony,
    RaceId.TharixCabal,
    RaceId.ChronosSeers,
    RaceId.NovaForgers,
    RaceId.AbyssalChorus,
    // Apex tier — only ever rolled by `pickRaceWithDepthBias` once the
    // sector distance pushes the curve past tier-4 race depth, but
    // included in `ALL_RACE_IDS` so every system that iterates the table
    // (NPCs, language, lore, traits, resources) treats them as first-class.
    RaceId.EidolonConclave,
    RaceId.AstralLeviathan,
    RaceId.SingularitySovereign,
];
export const RACE_RESOURCES = {
    [RaceId.Terran]: [
        ResourceTier.ScrapMetal, ResourceTier.IronOre, ResourceTier.CopperOre,
        ResourceTier.CrystalShards,
    ],
    [RaceId.Zyrkath]: [
        ResourceTier.PlasmaCore, ResourceTier.Bioflux, ResourceTier.VoidEssence,
    ],
    [RaceId.Luminara]: [
        ResourceTier.CrystalShards, ResourceTier.SolarPlasma,
        ResourceTier.ChronoQuartz, ResourceTier.QuantumFragment,
    ],
    [RaceId.Vokren]: [
        ResourceTier.ScrapMetal, ResourceTier.IronOre, ResourceTier.CopperOre,
        ResourceTier.Silicate, ResourceTier.TitaniumAlloy, ResourceTier.PlasmaCore,
    ],
    [RaceId.ObsidianCollective]: [
        ResourceTier.NeutroniumDust, ResourceTier.DarkMatter,
        ResourceTier.QuantumFragment, ResourceTier.EchoCrystal,
    ],
    [RaceId.VoidWalker]: [
        ResourceTier.VoidEssence, ResourceTier.VoidPearl, ResourceTier.DarkMatter,
        ResourceTier.AbyssalTear,
    ],
    [RaceId.Sylphari]: [
        ResourceTier.Bioflux, ResourceTier.CrystalShards, ResourceTier.Silicate,
    ],
    [RaceId.KrelgHegemony]: [
        ResourceTier.IronOre, ResourceTier.TitaniumAlloy, ResourceTier.PlasmaCore,
        ResourceTier.NeutroniumDust,
    ],
    [RaceId.TharixCabal]: [
        ResourceTier.Silicate, ResourceTier.PhasePrism, ResourceTier.ChronoQuartz,
    ],
    [RaceId.ChronosSeers]: [
        ResourceTier.ChronoQuartz, ResourceTier.EchoCrystal, ResourceTier.PhasePrism,
    ],
    [RaceId.NovaForgers]: [
        ResourceTier.SolarPlasma, ResourceTier.StarforgeIngot,
        ResourceTier.PlasmaCore, ResourceTier.TitaniumAlloy,
    ],
    [RaceId.AbyssalChorus]: [
        ResourceTier.AbyssalTear, ResourceTier.SingularityShard,
        ResourceTier.EtherealEssence, ResourceTier.VoidPearl,
    ],
    // Apex tiers reuse the highest-rarity resources so deep-sector farming
    // pulls the same exotic loot from any apex race — the difference is
    // the merchant-gate weapons each race sells, not the raw drop pool.
    [RaceId.EidolonConclave]: [
        ResourceTier.EtherealEssence, ResourceTier.PhasePrism,
        ResourceTier.EchoCrystal, ResourceTier.QuantumFragment,
    ],
    [RaceId.AstralLeviathan]: [
        ResourceTier.EtherealEssence, ResourceTier.AbyssalTear,
        ResourceTier.StarforgeIngot, ResourceTier.VoidPearl,
    ],
    [RaceId.SingularitySovereign]: [
        ResourceTier.SingularityShard, ResourceTier.DarkMatter,
        ResourceTier.AbyssalTear, ResourceTier.QuantumFragment,
    ],
};
const BASE_HP = 100;
const BASE_SHIELD = 50;
const BASE_DAMAGE = 5;
const BASE_SPEED = 100;
const HP_PER_LEVEL = 12;
const SHIELD_PER_LEVEL = 6;
const DAMAGE_PER_LEVEL = 0.8;
const SPEED_PER_LEVEL = 0.5;
export function playerStatsForLevel(level) {
    const lv = Math.max(1, level) - 1;
    return {
        maxHp: Math.round(BASE_HP + lv * HP_PER_LEVEL),
        maxShield: Math.round(BASE_SHIELD + lv * SHIELD_PER_LEVEL),
        damage: Math.round((BASE_DAMAGE + lv * DAMAGE_PER_LEVEL) * 10) / 10,
        speed: Math.round(BASE_SPEED + lv * SPEED_PER_LEVEL),
    };
}
export const RANK_NAMES = {
    [PlayerRank.Recruit]: { en: 'Recruit', es: 'Recluta', pt: 'Recruta' },
    [PlayerRank.Pilot]: { en: 'Pilot', es: 'Piloto', pt: 'Piloto' },
    [PlayerRank.Lieutenant]: { en: 'Lieutenant', es: 'Teniente', pt: 'Tenente' },
    [PlayerRank.Commander]: { en: 'Commander', es: 'Comandante', pt: 'Comandante' },
    [PlayerRank.Captain]: { en: 'Captain', es: 'Capitán', pt: 'Capitão' },
    [PlayerRank.Admiral]: { en: 'Admiral', es: 'Almirante', pt: 'Almirante' },
    [PlayerRank.FleetAdmiral]: { en: 'Fleet Admiral', es: 'Almirante de Flota', pt: 'Almirante de Frota' },
    [PlayerRank.Legend]: { en: 'Legend', es: 'Leyenda', pt: 'Lenda' },
};
//# sourceMappingURL=game.js.map